/**
 * Search creation utility functions
 * 
 * Handles the creation of new matchmaking searches and fetching
 * filtered providers. Extracted from useMatchSearch hook for better
 * code organization and reusability.
 */

import {
  createSearchPayload,
  extractProviderIds,
  createFilterValuesString,
} from './search-payload.utils';
import type { StepperAnswers } from '../hooks/useMatchmakingStateManagement';

import { GENERIC_SEARCH_ID } from '../constants/client-selection';

/**
 * Parameters for creating a new search
 *
 * Feature 060: Removed miles and selectedServiceRanges parameters
 * FEATURE 025: Added userType to route Care Seeker searches correctly
 */
type CreateSearchParams = {
  advisorProfileId: string;
  selectedClientId: string | typeof GENERIC_SEARCH_ID;
  matchingPrompt: string;
  selectedState: string;
  selectedCounty: string;
  selectedAddress: string;
  selectedZipCode: string;
  selectedLatitude: number | null;
  selectedLongitude: number | null;
  clientProfile: {
    relationship?: string | null;
    reasonForJoining?: string | null;
    ageRange?: string | null;
  } | undefined;
  answersToUse: StepperAnswers;
  userType?: 'CARE_SEEKER' | 'FINANCIAL_ADVISOR' | 'SERVICE_PROVIDER'; // FEATURE 025, 036: Routes searches for CS, FA, and SP
  createSearchMutation: any;
  filteredProvidersMutation: any;
};

/**
 * Result of search creation operation
 */
type CreateSearchResult = {
  searchId: number;
  filterValues: string | null;
  providersFound: number;
};

/**
 * Creates a new matchmaking search and fetches filtered providers
 * 
 * This function handles the initial search creation flow:
 * 1. Creates search payload with all filters and context
 * 2. Calls API to create the search
 * 3. Fetches filtered provider IDs based on search criteria
 * 4. Returns search ID and filter values for backend matchmaking proxy call
 * 
 * @param params - Search creation parameters
 * @returns Promise resolving to search result with searchId and filterValues
 * @throws Error if search creation or provider filtering fails
 */
export const createNewSearch = async (
  params: CreateSearchParams,
): Promise<CreateSearchResult> => {
  const {
    advisorProfileId,
    selectedClientId,
    matchingPrompt,
    selectedState,
    selectedCounty,
    selectedAddress,
    selectedZipCode,
    selectedLatitude,
    selectedLongitude,
    clientProfile,
    answersToUse,
    userType, // FEATURE 025: Pass through for API routing
    createSearchMutation,
    filteredProvidersMutation,
  } = params;

  // Create search payload with all location and context data
  const searchPayload = createSearchPayload(
    advisorProfileId,
    selectedClientId,
    matchingPrompt,
    selectedState,
    selectedCounty,
    selectedAddress,
    selectedZipCode,
    selectedLatitude,
    selectedLongitude,
    clientProfile,
    answersToUse,
    userType, // FEATURE 025: Pass through for API routing
  );

  // Create the search in backend
  const searchData = await createSearchMutation.mutateAsync(searchPayload);

  // Check if any providers were found
  if (searchData.providersFound === 0) {
    return {
      searchId: searchData.searchId,
      filterValues: null,
      providersFound: 0,
    };
  }

  // Fetch filtered providers that match the search criteria
  let filterValues: string | null = null;
  try {
    const filteredProvidersData = await filteredProvidersMutation.mutateAsync({
      searchId: searchData.searchId,
      advisorId: advisorProfileId,
      clientId: selectedClientId,
    });

    if (filteredProvidersData.providers && Array.isArray(filteredProvidersData.providers)) {
      const providerIds = extractProviderIds(filteredProvidersData.providers);
      if (providerIds.length > 0) {
        filterValues = createFilterValuesString(providerIds);
      } else {
        console.warn('[MATCHMAKING DEBUG] No provider IDs extracted!');
      }
    } else {
      console.warn('[MATCHMAKING DEBUG] No providers array or not an array!', filteredProvidersData);
    }
  } catch (filteredProvidersError) {
    console.error('Error fetching filtered providers:', filteredProvidersError);
    // Continue without filter values; backend matchmaking proxy will handle it
  }

  return {
    searchId: searchData.searchId,
    filterValues,
    providersFound: searchData.providersFound || 0,
  };
};

/**
 * Fetches filtered providers for an existing search (load more scenario)
 * 
 * Used when loading more results for an existing search.
 * 
 * @param params - Parameters for fetching filtered providers
 * @returns Promise resolving to filter values string or null
 * @throws Error if fetch fails
 */
export const fetchFilteredProvidersForSearch = async (params: {
  currentSearchId: number;
  advisorProfileId: string;
  selectedClientId: string | typeof GENERIC_SEARCH_ID;
  filteredProvidersMutation: any;
}): Promise<string | null> => {
  const { currentSearchId, advisorProfileId, selectedClientId, filteredProvidersMutation } = params;

  const filteredProvidersData = await filteredProvidersMutation.mutateAsync({
    searchId: currentSearchId,
    advisorId: advisorProfileId,
    clientId: selectedClientId,
  });

  if (filteredProvidersData.providers && Array.isArray(filteredProvidersData.providers)) {
    const providerIds = extractProviderIds(filteredProvidersData.providers);
    if (providerIds.length > 0) {
      return createFilterValuesString(providerIds);
    }
  }

  return null;
};

